import { Box, ButtonBase, Chip, Skeleton, TableCell, TableRow, Typography, alpha, useMediaQuery, useTheme } from '@mui/material';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import InboxRounded from '@mui/icons-material/InboxRounded';

// Listagens no celular (< 600px): no lugar da tabela, uma lista de linhas no
// mesmo padrão do LinhaVendaMobile (ver pages/Estoque/venda) — avatar |
// título com os detalhes embaixo | valor e etiqueta à direita. Tocar abre o
// registro. Uso nas páginas:
//   {smDown ? (
//     <ListaMobile linhas={rows} carregando={isLoading} chave={(r) => r.id}
//       vazio="Nenhum registro encontrado"
//       renderizar={(r) => <LinhaListagemMobile titulo={r.nome} detalhes={[...]} onClick={...} />} />
//   ) : (<Table>...</Table>)}

type TCor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

export interface ILinhaListagemMobileProps {
  titulo: React.ReactNode;
  // Linha de baixo do título: itens vazios somem, o resto é unido por " · ".
  detalhes?: (string | number | null | undefined | false)[];
  // Terceira linha opcional (ex.: aviso ou observação).
  extra?: React.ReactNode;
  valor?: React.ReactNode;
  corValor?: string;
  etiqueta?: { rotulo: string; cor?: TCor };
  // Texto pras iniciais do avatar, ou um ícone pronto. Sem nada, não mostra avatar.
  avatar?: string | React.ReactElement;
  // Cor do avatar/destaque (ex.: situação da linha).
  cor?: string;
  // Barra colorida na borda esquerda + fundo leve (linha em alerta, selecionada...).
  destaque?: boolean;
  // Botões à direita (excluir, baixar...) — o clique neles não abre a linha.
  acoes?: React.ReactNode;
  onClick?: () => void;
}

const iniciaisDe = (texto: string) => texto.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

export const LinhaListagemMobile: React.FC<ILinhaListagemMobileProps> = ({
  titulo, detalhes, extra, valor, corValor, etiqueta, avatar, cor, destaque, acoes, onClick,
}) => {
  const theme = useTheme();
  const corBase = cor || theme.palette.primary.main;
  const textoDetalhes = (detalhes || []).filter((d) => d !== null && d !== undefined && d !== false && d !== '').join(' · ');

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        ...(destaque && { bgcolor: alpha(corBase, 0.08), boxShadow: `inset 3px 0 0 ${corBase}` }),
      }}
    >
      <ButtonBase
        onClick={onClick}
        disabled={!onClick}
        sx={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.25,
          justifyContent: 'flex-start', textAlign: 'left', pl: 2, pr: acoes ? 0.5 : 1, py: 1.25,
        }}
      >
        {avatar !== undefined && (
          <Box
            sx={{
              width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.8rem', fontWeight: 700,
              bgcolor: alpha(corBase, 0.12), color: corBase,
              '& svg': { fontSize: 20 },
            }}
          >
            {typeof avatar === 'string' ? (iniciaisDe(avatar) || '?') : avatar}
          </Box>
        )}
        <Box flex={1} minWidth={0}>
          <Typography variant="body2" fontWeight={700} noWrap component="div">{titulo}</Typography>
          {textoDetalhes && <Typography variant="caption" color="text.secondary" noWrap component="div">{textoDetalhes}</Typography>}
          {extra && <Typography variant="caption" noWrap component="div">{extra}</Typography>}
        </Box>
        {(valor !== undefined || etiqueta) && (
          <Box textAlign="right" flexShrink={0} display="flex" flexDirection="column" alignItems="flex-end" gap={0.25}>
            {valor !== undefined && (
              <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: 'nowrap', color: corValor || 'text.primary' }}>{valor}</Typography>
            )}
            {etiqueta && (
              <Chip size="small" label={etiqueta.rotulo} color={etiqueta.cor || 'default'} variant="outlined"
                sx={{ height: 18, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.75 } }} />
            )}
          </Box>
        )}
        {onClick && !acoes && <ChevronRightRounded sx={{ color: 'text.disabled', ml: -0.5 }} />}
      </ButtonBase>
      {acoes && (
        <Box display="flex" alignItems="center" pr={1} onClick={(e) => e.stopPropagation()}>{acoes}</Box>
      )}
    </Box>
  );
};

interface IListaMobileProps<T> {
  linhas: T[];
  carregando?: boolean;
  chave: (linha: T, indice: number) => string | number;
  renderizar: (linha: T, indice: number) => React.ReactNode;
  // Sem `vazio`, a lista não mostra nada quando vazia (a página já tem a própria linha de "nenhum registro").
  vazio?: string;
  vazioDetalhe?: string;
}

export const ListaMobile = <T,>({ linhas, carregando, chave, renderizar, vazio, vazioDetalhe }: IListaMobileProps<T>) => (
  <Box>
    {carregando && linhas.length === 0 && Array.from({ length: 6 }).map((_, i) => (
      <Box key={i} display="flex" alignItems="center" gap={1.25} px={2} py={1.25}>
        <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '12px' }} />
        <Box flex={1}><Skeleton width="60%" /><Skeleton width="40%" /></Box>
        <Skeleton width={60} />
      </Box>
    ))}
    {linhas.map((linha, i) => <Box key={chave(linha, i)}>{renderizar(linha, i)}</Box>)}
    {vazio && !carregando && linhas.length === 0 && (
      <Box textAlign="center" py={6} px={3}>
        <InboxRounded sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography fontWeight={700}>{vazio}</Typography>
        {vazioDetalhe && <Typography variant="body2" color="text.secondary">{vazioDetalhe}</Typography>}
      </Box>
    )}
  </Box>
);

// A lista dentro do <TableBody> da listagem (no lugar das linhas da tabela),
// pra manter o rodapé da tabela (paginação, carregando) como está. Usar com
// o <TableHead> escondido no celular.
export const CelulaListaMobile: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TableRow>
    <TableCell colSpan={100} sx={{ p: 0, borderBottom: 0 }}>{children}</TableCell>
  </TableRow>
);

// Formatação usada nas linhas.
export const moedaMobile = (valor: unknown) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// true no celular (< 600px) — mesmo limite do breakpoint 'sm' do tema.
export const useCelular = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down('sm'));
};
